import clsx from "clsx";
import { motion, type TargetAndTransition } from "motion/react";
import React from "react";

const variants: Record<string, TargetAndTransition> = {
	spinRotation: {
		rotate: [0, 180, 360],
		transition: {
			bounce: 1,
			duration: 1,
			ease: "circInOut",
			repeat: Number.POSITIVE_INFINITY,
			repeatDelay: 0,
			repeatType: "loop",
		},
	},
	staticRotation: {
		rotate: 0,
	},
};

/** Props Interface */
interface LogoProps {
	height?: "14" | "18" | "24" | "28" | "32" | "36" | "40" | "48" | "56" | "64";
	variant?: keyof typeof variants;
	withText?: boolean;
	layoutId?: string;
}

export const Logo: React.FC<LogoProps> = ({
	height = "28",
	variant = "staticRotation",
	withText = true,
	layoutId,
}) => {
	/** Render */
	return (
		<motion.div
			layoutId={layoutId}
			className={clsx(
				"flex flex-row justify-center items-center gap-2 cursor-default branding-logo",
			)}
		>
			<svg
				height={height}
				viewBox={withText ? "0 0 1264 414" : "0 0 414 414"}
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<title>Logo</title>
				<motion.g
					initial={{ rotate: 0 }}
					variants={variants}
					animate={variant}
					whileHover={variants.spinRotation}
				>
					<path
						d="M176.924 17.5505C195.488 6.83253 218.36 6.83252 236.924 17.5505L355.992 86.2947C374.556 97.0127 385.992 116.82 385.992 138.256V275.745C385.992 297.181 374.556 316.988 355.992 327.706L236.924 396.45C218.36 407.168 195.488 407.168 176.924 396.45L57.8553 327.706C39.2913 316.988 27.8553 297.181 27.8553 275.745V138.256C27.8553 116.82 39.2913 97.0127 57.8553 86.2947L176.924 17.5505Z"
						fill="#D9D9D9"
						fillOpacity="0.000005"
					/>
					<path
						d="M27.8555 210.665C121.043 232.177 192.593 308.661 206.876 404.488C196.533 404.48 186.192 401.801 176.924 396.451L57.8555 327.707C39.2914 316.989 27.8555 297.181 27.8555 275.745V210.665ZM385.992 275.745C385.992 297.181 374.556 316.989 355.992 327.707L236.924 396.451C227.656 401.802 217.314 404.48 206.971 404.488C221.254 308.661 292.804 232.177 385.992 210.665V275.745ZM206.924 9.51221C192.749 105.484 121.144 182.109 27.8555 203.644V138.256C27.8555 116.82 39.2914 97.0124 57.8555 86.2944L176.924 17.5503C186.206 12.1914 196.565 9.51221 206.924 9.51221ZM206.924 9.51221C217.283 9.51221 227.642 12.1914 236.924 17.5503L355.992 86.2944C374.556 97.0124 385.992 116.82 385.992 138.256V203.644C292.704 182.109 221.099 105.484 206.924 9.51221Z"
						fill="url(#paint0_linear_81152_56)"
					/>
				</motion.g>
				{withText && (
					<React.Fragment>
						<path
							d="M1215.17 196.155C1212.9 183.915 1208.82 174.621 1202.93 168.275C1197.26 161.928 1188.54 158.755 1176.75 158.755C1169.04 158.755 1162.58 160.115 1157.37 162.835C1152.38 165.328 1148.3 168.501 1145.13 172.355C1142.18 176.208 1140.03 180.288 1138.67 184.595C1137.54 188.901 1136.86 192.755 1136.63 196.155H1215.17ZM1136.63 226.755C1137.31 242.395 1141.28 253.728 1148.53 260.755C1155.78 267.781 1166.21 271.295 1179.81 271.295C1189.56 271.295 1197.94 268.915 1204.97 264.155C1212 259.168 1216.3 253.955 1217.89 248.515H1260.39C1253.59 269.595 1243.16 284.668 1229.11 293.735C1215.06 302.801 1198.06 307.335 1178.11 307.335C1164.28 307.335 1151.82 305.181 1140.71 300.875C1129.6 296.341 1120.2 289.995 1112.49 281.835C1104.78 273.675 1098.78 263.928 1094.47 252.595C1090.39 241.261 1088.35 228.795 1088.35 215.195C1088.35 202.048 1090.5 189.808 1094.81 178.475C1099.12 167.141 1105.24 157.395 1113.17 149.235C1121.1 140.848 1130.51 134.275 1141.39 129.515C1152.5 124.755 1164.74 122.375 1178.11 122.375C1193.07 122.375 1206.1 125.321 1217.21 131.215C1228.32 136.881 1237.38 144.588 1244.41 154.335C1251.66 164.081 1256.88 175.188 1260.05 187.655C1263.22 200.121 1264.36 213.155 1263.45 226.755H1136.63Z"
							className="fill-black dark:fill-white"
						/>
						<path
							d="M1038.52 127.135H1073.88V159.435H1038.52V246.475C1038.52 254.635 1039.88 260.075 1042.6 262.795C1045.32 265.515 1050.76 266.875 1058.92 266.875C1061.64 266.875 1064.25 266.761 1066.74 266.535C1069.23 266.308 1071.61 265.968 1073.88 265.515V302.915C1069.8 303.595 1065.27 304.048 1060.28 304.275C1055.29 304.501 1050.42 304.615 1045.66 304.615C1038.18 304.615 1031.04 304.048 1024.24 302.915C1017.67 302.008 1011.77 300.081 1006.56 297.135C1001.57 294.188 997.606 289.995 994.659 284.555C991.712 279.115 990.239 271.975 990.239 263.135V159.435H960.999V127.135H990.239V74.4348H1038.52V127.135Z"
							className="fill-black dark:fill-white"
						/>
						<path
							d="M939.303 99.9348H891.023V60.1548H939.303V99.9348ZM891.023 127.135H939.303V302.915H891.023V127.135Z"
							className="fill-black dark:fill-white"
						/>
						<path
							d="M853.02 302.915H807.12V278.435H806.1C799.98 288.635 792.047 296.001 782.3 300.535C772.553 305.068 762.58 307.335 752.38 307.335C739.46 307.335 728.807 305.635 720.42 302.235C712.26 298.835 705.8 294.075 701.04 287.955C696.28 281.608 692.88 274.015 690.84 265.175C689.027 256.108 688.12 246.135 688.12 235.255V127.135H736.4V226.415C736.4 240.921 738.667 251.801 743.2 259.055C747.733 266.081 755.78 269.595 767.34 269.595C780.487 269.595 790.007 265.741 795.9 258.035C801.793 250.101 804.74 237.181 804.74 219.275V127.135H853.02V302.915Z"
							className="fill-black dark:fill-white"
						/>
						<path
							d="M542.903 245.795C542.903 251.008 543.923 255.541 545.963 259.395C548.23 263.021 551.063 266.081 554.463 268.575C557.863 270.841 561.716 272.541 566.023 273.675C570.556 274.808 575.203 275.375 579.963 275.375C583.363 275.375 586.876 275.035 590.503 274.355C594.356 273.448 597.756 272.201 600.703 270.615C603.876 268.801 606.483 266.535 608.523 263.815C610.563 260.868 611.583 257.241 611.583 252.935C611.583 245.681 606.71 240.241 596.963 236.615C587.443 232.988 574.07 229.361 556.843 225.735C549.816 224.148 542.903 222.335 536.103 220.295C529.53 218.028 523.636 215.195 518.423 211.795C513.21 208.168 509.016 203.748 505.843 198.535C502.67 193.095 501.083 186.521 501.083 178.815C501.083 167.481 503.236 158.188 507.543 150.935C512.076 143.681 517.97 138.015 525.223 133.935C532.476 129.628 540.636 126.681 549.703 125.095C558.77 123.281 568.063 122.375 577.583 122.375C587.103 122.375 596.283 123.281 605.123 125.095C614.19 126.908 622.236 129.968 629.263 134.275C636.29 138.581 642.07 144.361 646.603 151.615C651.363 158.641 654.196 167.595 655.103 178.475H609.203C608.523 169.181 605.01 162.948 598.663 159.775C592.316 156.375 584.836 154.675 576.223 154.675C573.503 154.675 570.556 154.901 567.383 155.355C564.21 155.581 561.263 156.261 558.543 157.395C556.05 158.528 553.896 160.228 552.083 162.495C550.27 164.535 549.363 167.368 549.363 170.995C549.363 175.301 550.95 178.815 554.123 181.535C557.296 184.255 561.376 186.521 566.363 188.335C571.576 189.921 577.47 191.395 584.043 192.755C590.616 194.115 597.303 195.588 604.103 197.175C611.13 198.761 617.93 200.688 624.503 202.955C631.303 205.221 637.31 208.281 642.523 212.135C647.736 215.761 651.93 220.408 655.103 226.075C658.276 231.515 659.863 238.315 659.863 246.475C659.863 258.035 657.483 267.781 652.723 275.715C648.19 283.421 642.183 289.655 634.703 294.415C627.223 299.175 618.61 302.461 608.863 304.275C599.343 306.315 589.596 307.335 579.623 307.335C569.423 307.335 559.45 306.315 549.703 304.275C539.956 302.235 531.23 298.835 523.523 294.075C516.043 289.315 509.81 283.081 504.823 275.375C500.063 267.441 497.456 257.581 497.003 245.795H542.903Z"
							className="fill-black dark:fill-white"
						/>
					</React.Fragment>
				)}
				<defs>
					<linearGradient
						id="paint0_linear_81152_56"
						x1="27.8555"
						y1="9.51221"
						x2="385.991"
						y2="404.489"
						gradientUnits="userSpaceOnUse"
					>
						<stop stopColor="var(--color-logo-gradient-start)" />
						<stop
							offset="1"
							stopColor="var(--color-logo-gradient-end)"
						/>
					</linearGradient>
				</defs>
			</svg>
		</motion.div>
	);
};
